import { ChangeDetectionStrategy, Component, HostBinding, OnInit } from '@angular/core';

import { GameType } from '@bpos/v1/sports-promo';
import { AccaBoostConfig, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { Cards } from '@frontend/sports/types/components/sitecore';
import { UserService } from '@frontend/sports/user/feature';
import { IntlService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { AccaBoostRatioPipe } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-ratio.pipe';
import { TokensSummaryService } from 'packages/sports/web/app/src/tokens-base/tokens-summary.service';
import { Observable, combineLatest, distinctUntilChanged, map } from 'rxjs';

import { CurrencyPipe } from '../../../../../../base/pipes/currency.pipe';
import { IBetslipRootState } from '../../../../../../base/store/state';
import { AccaBoostSummary, AccaBoostSummaryService } from '../../../../../acca-boost/services/acca-boost-summary.service';
import { betslipPicksListCountSelector } from '../../../../../picks/selectors';
import { AccaBoostToken } from '../../../../reward-tokens.model';
import { TokenCategories } from '../../../../services/reward-tokens-banner.service';
import { getAccaBoostRatio } from '../../../../services/reward-tokens.utils';
import { tokenBoostAnimations } from '../../token-boost-animations';
import { RewardTokenCardBaseComponent } from '../reward-token-card-base.component';
import { RewardTokenLimitation } from '../reward-token-limitations.component';

interface AccaBoostCardViewModel {
    boostedWinnings: Decimal | null;
    originalWinnings: Decimal | null;
    maxReward: number;
    numberOfLegs: number;
    currentBoostPercentage: string;
    maxBoostPercentage: string;
    title: string;
    gameType?: GameType;
}

@Component({
    selector: 'bs-acca-boost-card',
    templateUrl: './acca-boost-card.component.html',
    styleUrls: ['../../cards/styles/reward-token-card-shared.scss', './acca-boost-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [tokenBoostAnimations],
    standalone: false,
})
export class AccaBoostCardComponent extends RewardTokenCardBaseComponent<AccaBoostToken> implements OnInit {
    @HostBinding('class') className = 'acca-boost-card';

    model$: Observable<AccaBoostCardViewModel>;

    constructor(
        public override sitecore: Sitecore,
        protected override crmConfig: CrmConfig,
        protected userService: UserService,
        protected override intlService: IntlService,
        private currencyPipe: CurrencyPipe,
        private summaryService: AccaBoostSummaryService,
        private accaBoostRatioFormatter: AccaBoostRatioPipe,
        private store: Store<IBetslipRootState>,
        protected override oddsFormatterService: OddsFormatterService,
        public accaBoostConfig: AccaBoostConfig,
        protected override tokensPayoutInfo: TokensSummaryService,
    ) {
        super(sitecore, crmConfig, userService, intlService, oddsFormatterService, tokensPayoutInfo);
    }

    ngOnInit(): void {
        const pickCount$ = this.store.select(betslipPicksListCountSelector).pipe(distinctUntilChanged());

        this.model$ = combineLatest([this.summaryService.getSummary$(), pickCount$]).pipe(
            map(([summary, pickCount]) => this.mapViewModel(summary, pickCount)),
        );
    }

    private mapViewModel = (summary: AccaBoostSummary | null, pickCount: number): AccaBoostCardViewModel => {
        const maxBoostRatio = this.token.boostLadder[this.token.boostLadder.length - 1].ratio;
        const boostRatio = getAccaBoostRatio(this.token, pickCount).toNumber();
        const boostPercentage = this.accaBoostRatioFormatter.transform(boostRatio, false);

        return {
            title: this.accaBoostConfig.isEnhancedLayoutEnabled
                ? this.accaBoostRatioFormatter.transform(boostRatio, false, this.sitecore.accaBoost.AccaBoostPercentageTitle)
                : this.sitecore.accaBoost.AccaBoost,
            originalWinnings: summary?.originalWinningsNet?.isZero() ? null : summary?.originalWinningsNet || null,
            boostedWinnings: summary?.boostedWinningsNet?.isZero() ? null : summary?.boostedWinningsNet || null,
            numberOfLegs: pickCount,
            currentBoostPercentage: boostPercentage,
            maxReward: this.token.maxReward,
            maxBoostPercentage: this.accaBoostRatioFormatter.transform(maxBoostRatio, false),
            gameType: this.token.filter.gameType,
        };
    };

    getTokenCategories(model?: AccaBoostCardViewModel): TokenCategories {
        if (this.accaBoostConfig.hideCategoriesInTokenDrawer) {
            return {};
        }

        const categories: TokenCategories = {};
        const tokenCards: Cards = this.sitecore.crmTokens.cards;
        const separator = tokenCards.TokenCategoryDelimiter;

        if (model?.gameType && model?.gameType !== GameType.Mixed) {
            categories.prematchCategory =
                model.gameType === GameType.InPlay ? this.sitecore.crmTokens.cards.Live : this.sitecore.crmTokens.cards.Prematch;
        }

        categories.sportsCategory = this.token.filter.sports.map((s) => s.name)?.join(separator);

        return categories;
    }

    override getTokenLimitations(model?: AccaBoostCardViewModel): RewardTokenLimitation[] {
        return model
            ? [
                  {
                      name: this.sitecore.accaBoost.TokensDialogNoOfLegs,
                      value: model.numberOfLegs.toString(),
                  },
                  {
                      name: this.sitecore.accaBoost.TokensDialogBoost,
                      value: model.currentBoostPercentage,
                  },
                  {
                      name: this.sitecore.accaBoost.TokensDialogMaxReward,
                      value: this.currencyPipe.transform(model.maxReward, true),
                  },
                  {
                      name: this.sitecore.accaBoost.TokensDialogMaxBoost,
                      value: model.maxBoostPercentage,
                  },
              ]
            : [];
    }
}
